// src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [username, setUsername] = useState('');

  const { token } = useParams();
  const navigate = useNavigate();


  useEffect(() => {
    if (!token) return;

    const verifyToken = async () => {
      try {
        setIsLoading(true);

        const url = `/api/auth/reset-password/${token}`;
        const response = await fetch(url);
        const data = await response.json();

        if (response.ok) {
          setTokenValid(true);
          setUsername(data.username || '');
        } else {
          setTokenValid(false);
          setIsError(true);
          setMessage(data.message || 'Invalid or expired reset link');
        }
      } catch (error) {
        setTokenValid(false);
        setIsError(true);
        setMessage('Failed to verify reset link');
      } finally {
        setIsLoading(false);
        setTokenChecked(true);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsError(false);

    if (password !== confirmPassword) {
      setIsError(true);
      setMessage('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setIsError(true);
      setMessage('Password must be at least 6 characters long');
      return;
    }

    try {
      setIsLoading(true);

      const url = `/api/auth/reset-password/${token}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsError(false);
        setMessage('Password reset successful! Redirecting to login...');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setIsError(true);
        setMessage(data.message || 'Failed to reset password');
      }
    } catch (error) {
      setIsError(true);
      setMessage('An error occurred while resetting your password');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !tokenChecked) {
    return (
      <div className="max-w-md mx-auto mt-10 bg-gray-700 p-6 rounded-lg shadow-md text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-white">Verifying reset link...</p>
      </div>
    );
  }

  if (!tokenValid && tokenChecked) {
    return (
      <div className="max-w-md mx-auto mt-10 bg-gray-700 p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6 text-center text-white">Password Reset</h2>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {message || 'This password reset link is invalid or has expired.'}
        </div>
        <div className="text-center mt-6">
          <Link to="/login" className="text-blue-400 hover:underline">
            Return to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-10 bg-gray-700 p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-center text-white">Reset Password</h2>

      {username && (
        <p className="mb-4 text-center text-white">
          Reset password for <span className="font-semibold">{username}</span>
        </p>
      )}

      {message && (
        <div
          className={`px-4 py-3 rounded mb-4 ${
            isError
              ? 'bg-red-100 border border-red-400 text-red-700'
              : 'bg-green-100 border border-green-400 text-green-700'
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-white mb-2">New Password:</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-2 border rounded text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter new password"
            required
            minLength="6"
          />
        </div>

        <div className="mb-6">
          <label className="block text-white mb-2">Confirm Password:</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full p-2 border rounded text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Confirm new password"
            required
            minLength="6"
          />
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
          disabled={isLoading}
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="!text-red-500 hover:underline visited:!text-red-500"
          style={{ color: '#ef4444 !important' }}
        >
          Return to Login
        </Link>
      </div>
    </div>
  );
}

export default ResetPassword;
